import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { message } from 'antd';
import ServicesApi from 'libs/api/website/services';
import AuthService from 'services/Auth';

const useServices = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [filters, setFilters] = useState({});
  const [filterData, setFilterData] = useState({
    shopping_centers: [],
    chains: [],
    companies: [],
  });

  const navigate = useNavigate();
  const paramsRef = useRef({});

  const checkAuth = useCallback(() => {
    const token = AuthService.getToken();
    if (!token) {
      message.error('You are not authorized. Please login.');
      navigate('/login');
      return false;
    }
    return true;
  }, [navigate]);

  const fetchServices = async (params = {}) => {
    if (!checkAuth()) return;

    setLoading(true);
    try {
      const response = await ServicesApi.list(params);

      if (response.data.success) {
        setServices(response.data.services || []);
        setTotal(response.data.pagination?.total_count || 0);
      } else {
        setServices([]);
        setTotal(0);
      }
    } catch (error) {
      console.error('Error fetching services:', error);
      if (error.response?.status === 401) {
        message.error('You are not authorized to access this page.');
        navigate('/login');
      } else {
        message.error(
          error.response?.data?.message || 'Failed to load services',
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchService = useCallback(
    async (id) => {
      if (!checkAuth()) return null;

      try {
        const response = await ServicesApi.show(id);
        console.log('Fetch service response:', response.data);
        if (response.data.success) {
          return {
            ...response.data.service,
            icon: response.data.icon,
            icon_name: response.data.icon_name,
            icon_id: response.data.icon_id,
          };
        }
        return null;
      } catch (error) {
        console.error('Error fetching service:', error);
        if (error.response?.status === 401) {
          message.error('You are not authorized to access this page.');
          navigate('/login');
        } else {
          message.error(
            error.response?.data?.message || 'Failed to load service',
          );
        }
        return null;
      }
    },
    [checkAuth, navigate],
  );

  const createService = useCallback(
    async (data) => {
      if (!checkAuth()) return null;

      try {
        const response = await ServicesApi.create(data);
        if (response.data.success) {
          message.success('Service created successfully');
          return response.data.service;
        }
        return null;
      } catch (error) {
        console.error('Error creating service:', error);
        if (error.response?.status === 401) {
          message.error('You are not authorized to access this page.');
          navigate('/login');
        } else {
          message.error(
            error.response?.data?.message || 'Failed to create service',
          );
        }
        return null;
      }
    },
    [checkAuth, navigate],
  );

  const updateService = useCallback(
    async (id, data) => {
      if (!checkAuth()) return null;

      try {
        console.log('Updating service with data:', data);
        // Log FormData entries
        if (data instanceof FormData) {
          console.log('FormData entries:');
          for (let pair of data.entries()) {
            console.log(
              pair[0],
              ':',
              pair[1] instanceof File ? `File(${pair[1].name})` : pair[1],
            );
          }
        }
        const response = await ServicesApi.update(id, data);
        console.log('Update response:', response);
        if (response.data.success) {
          message.success('Service updated successfully');
          return response.data.service;
        }
        return null;
      } catch (error) {
        console.error('Error updating service:', error);
        console.error('Error response:', error.response);
        if (error.response?.status === 401) {
          message.error('You are not authorized to access this page.');
          navigate('/login');
        } else {
          message.error(
            error.response?.data?.message || 'Failed to update service',
          );
        }
        return null;
      }
    },
    [checkAuth, navigate],
  );

  const deleteFile = useCallback(
    async (id, fileId) => {
      if (!checkAuth()) return null;

      try {
        const response = await ServicesApi.deleteFile(id, fileId);
        if (response.data.success) {
          message.success('File deleted successfully');
          return true;
        }
        return false;
      } catch (error) {
        console.error('Error deleting file:', error);
        if (error.response?.status === 401) {
          message.error('You are not authorized to access this page.');
          navigate('/login');
        } else {
          message.error(
            error.response?.data?.message || 'Failed to delete file',
          );
        }
        return false;
      }
    },
    [checkAuth, navigate],
  );

  const deleteService = useCallback(
    async (id) => {
      if (!checkAuth()) return false;

      try {
        console.log('Deleting service with ID:', id);
        const response = await ServicesApi.delete(id);
        console.log('Delete response:', response);
        if (response.data.success) {
          message.success('Service deleted successfully');

          if (services.length === 1 && currentPage > 1) {
            setCurrentPage((prev) => prev - 1);
          } else {
            console.log('Fetching services with params:', paramsRef.current);
            fetchServices(paramsRef.current);
          }
          return true;
        }
        return false;
      } catch (error) {
        console.error('Error deleting service:', error);
        if (error.response?.status === 401) {
          message.error('You are not authorized to access this page.');
          navigate('/login');
        } else {
          message.error(
            error.response?.data?.message || 'Failed to delete service',
          );
        }
        return false;
      }
    },
    [checkAuth, navigate, services.length, currentPage],
  );

  // Load filter data (shopping centers, chains)
  const loadFilterData = useCallback(async () => {
    try {
      const response = await ServicesApi.getFilters();
      if (response.data) {
        setFilterData({
          shopping_centers: response.data.shopping_centers || [],
          chains: response.data.chains || [],
          companies: response.data.companies || [],
        });
      }
    } catch (error) {
      console.error('Error loading filter data:', error);
    }
  }, []);

  useEffect(() => {
    loadFilterData();
  }, [loadFilterData]);

  useEffect(() => {
    const params = {
      page: currentPage,
      per: pageSize,
    };
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params[key] = value;
      }
    });
    paramsRef.current = params;
    fetchServices(params);
  }, [currentPage, pageSize, filters]);

  const updateFilters = useCallback((newFilters) => {
    if (Object.keys(newFilters).length === 0) {
      setFilters({});
    } else {
      setFilters((prev) => ({ ...prev, ...newFilters }));
    }
    setCurrentPage(1);
  }, []);

  const handlePageChange = useCallback((page, size) => {
    setCurrentPage(page);
    setPageSize(size);
  }, []);

  return {
    services,
    loading,
    total,
    currentPage,
    pageSize,
    filters,
    filterData,
    updateFilters,
    handlePageChange,
    fetchService,
    createService,
    updateService,
    deleteFile,
    refetch: () => fetchServices(paramsRef.current),
    deleteService,
  };
};

export default useServices;
